import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { getTimestamp } from '../../../../../../helpers/date'
import { IconChoices } from '../../../../../common/Icon'
import { Button } from '../../../../../common/Button'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { PostImages } from '../../../../PostImages'
import { PostDocuments } from '../../../../PostDocuments'
import { PostEvents } from '../../../../PostEvents'
import { PostPolls } from '../../../../PostPolls'
import { theme } from '../../../../../../theme/theme'
import '../../../../Post/post.css'
import { PostHeader } from '../../../../Post/PostHeader'
import { PostText } from '../../../../Post/PostText'
import { PostData } from '../post-data'
import { usePreviewPostRow } from '../usePreviewPostRow'
import { userQueries } from '../../../../../../api/users'
import { ISocietiesModel } from '../../../../../../interfaces/models/societies.interfaces'

interface PostProps {
  postData: PostData
  society: ISocietiesModel
}

export const PreviewPostModalContent = ({
  postData,
  society,
}: PostProps): JSX.Element | null => {
  const { translate } = useAppTranslation()
  const { data: author } = useQuery(userQueries.getUser(postData.author))
  const row = usePreviewPostRow(postData, society, author)

  if (!author) {
    return null
  }

  return (
    <div className="my-4 flex flex-col gap-4 rounded-md border border-neutral-80 bg-white p-4 md:p-6">
      <PostHeader row={row} disabled />
      <div className="post-body mt-0 flex max-w-full flex-col gap-4">
        <PostText message={row.message} isAutomaticallyCreatedPost={false} />
        <PostImages media={row.media} createdAt={row.createdAt} disabled />
        <PostDocuments documents={row.documents} disabled />
        <PostEvents events={row.events} disabled />
        <PostPolls postId={row.id} polls={row.polls} disabled />
      </div>
      <div className="border-b border-neutral-80" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex gap-3">
          <Button
            label={translate('common.like')}
            icon={IconChoices.LIKE}
            disabled
          />
          <Button
            label={translate('common.makeComment')}
            icon={IconChoices.COMMENT}
            disabled
          />
        </div>
        {postData.updatedAt && postData.updatedAt > postData.createdAt && (
          <p
            style={theme.textVariants.fineprint}
            className="mt-2 text-neutral-30 md:mt-0"
          >
            {translate('post.editedPrefix')}{' '}
            {getTimestamp(new Date(postData.updatedAt))}
          </p>
        )}
      </div>
    </div>
  )
}
